// frontend/src/components/capacitor/CapacitorPreview.tsx
import { useState } from 'react'
import { CapacitorFeaturesDrawer } from './CapacitorFeaturesDrawer'
import { Drawer } from 'vaul'
import { Loader2, Settings2 } from 'lucide-react'
import { ChatPreview } from '../ChatPreview'
import { CapacitorMediaSheet } from './CapacitorMediaSheet'
import { ParseQualityNotice } from '../ParseQualityNotice'
import { WhatsAppMessage, PreviewOptions, ChatStats, ParseQuality } from '../../types/preview'
import { BillingProduct } from '../../hooks/useBilling'
import { PREMIUM_PLUS_ENABLED } from '../../featureFlags'
import '../ChatPreview.css'
import './Capacitor.css'

const SWATCHES_EXPORTEUR = ['#dcf8c6','#cce5ff','#ffe0b2','#e8d5f5','#ffd6d6','#d5f5e3','#fff9c4','#f0f0f0']
const SWATCHES_OTHER     = ['#ffffff','#f0f4ff','#fff3e0','#f3e5f5','#e8f5e9','#fce4ec','#f1f8e9','#e0f7fa']
const SWATCHES_BG        = ['#e5ddd5','#ffffff','#f5f5dc','#fef9ef','#e8f4f8','#e8f5e9','#fce4ec','#ede7f6']

function SwatchRow({ label, value, swatches, onChange }: {
  label: string, value: string, swatches: string[], onChange: (c: string) => void
}) {
  return (
    <div className="cp-swatch-row">
      <span className="cp-swatch-label">{label}</span>
      <div className="cp-swatch-grid">
        {swatches.map(c => (
          <button
            key={c}
            className={`cp-swatch${value === c ? ' active' : ''}`}
            style={{ background: c }}
            onClick={() => onChange(c)}
          />
        ))}
      </div>
    </div>
  )
}

interface Props {
  messages: WhatsAppMessage[]
  stats: ChatStats
  quality?: ParseQuality | null
  options: PreviewOptions
  setOptions: (opts: PreviewOptions) => void
  messageCount: number
  onPurchase: (productId: string) => void
  billingProducts: BillingProduct[]
  isRendering: boolean
  onDesktopHandoff: () => void
}

export function CapacitorPreview({
  messages, stats, quality, options, setOptions,
  onPurchase, billingProducts, isRendering, onDesktopHandoff,
}: Props) {
  const [mediaSheetOpen, setMediaSheetOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [purchaseSheetOpen, setPurchaseSheetOpen] = useState(false)
  const [featuresOpen, setFeaturesOpen] = useState(false)
  const [plusDetailsOpen, setPlusDetailsOpen] = useState(false)

  const set = (patch: Partial<PreviewOptions>) => setOptions({ ...options, ...patch })

  return (
    <>
      <div className="cap-preview">
        {/* Scrollable chat area */}
        <div className="cap-preview-scroll">
          <div className="cap-preview-watermark">ChatArchiv VORSCHAU</div>
          <ParseQualityNotice quality={quality} />
          <ChatPreview messages={messages} options={options} stats={stats} isFreeVersion={true} />
        </div>
      </div>

      {/* Bottom bar is a direct child of cap-shell (via fragment) — avoids overflow:hidden blocking pointer events */}
      <div className="cp-bar">
          {/* Color rows: full-width 2-column grid */}
          <div className="cp-colors-grid">
            <div className="cp-color-group">
              <span className="cp-color-label">Ich</span>
              <div className="cp-dot-row">
                {SWATCHES_EXPORTEUR.slice(0, 6).map(c => (
                  <button key={c} className={`cp-dot${options.exporteurColor === c ? ' active' : ''}`}
                    style={{ background: c }} onClick={() => set({ exporteurColor: c })} />
                ))}
              </div>
            </div>
            <div className="cp-color-group">
              <span className="cp-color-label">Andere</span>
              <div className="cp-dot-row">
                {SWATCHES_OTHER.slice(0, 6).map(c => (
                  <button key={c} className={`cp-dot${options.otherColor === c ? ' active' : ''}`}
                    style={{ background: c }} onClick={() => set({ otherColor: c })} />
                ))}
              </div>
            </div>
          </div>

          {/* Actions row */}
          <div className="cp-bar-actions">
            <div className="cap-font-size">
              {(['small', 'normal', 'large'] as const).map((size, i) => (
                <button key={size} type="button"
                  className={options.fontSize === size ? 'active' : ''}
                  onClick={() => set({ fontSize: size })}
                  style={{ fontSize: [12, 14, 16][i] }}>
                  {['A−', 'A', 'A+'][i]}
                </button>
              ))}
            </div>
            <div className="cp-bar-actions-right">
              <button type="button" className="cp-icon-btn" onClick={() => setSettingsOpen(true)}>
                <Settings2 size={20} />
              </button>
              <button type="button" className="cp-icon-btn" onClick={() => setMediaSheetOpen(true)}>
                📎
              </button>
            </div>
          </div>

          {/* PDF CTA — ein Button */}
          <button
            type="button"
            className="cap-cta-btn"
            onClick={() => setPurchaseSheetOpen(true)}
            disabled={isRendering}
          >
            {isRendering
              ? <><Loader2 size={18} className="spinning" /> PDF wird erstellt…</>
              : '📄 PDF kaufen'}
          </button>
        </div>

      {/* Vaul Settings Drawer */}
      <Drawer.Root open={settingsOpen} onOpenChange={setSettingsOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="cp-drawer-overlay" />
          <Drawer.Content className="cp-drawer">
            <div className="cp-drawer-handle" />
            <Drawer.Title className="cp-drawer-title">Einstellungen</Drawer.Title>

            <div className="cp-drawer-body">
              {/* Chat title */}
              <div className="cp-drawer-field">
                <label className="cp-drawer-field-label">Chat-Titel</label>
                <input
                  className="cp-drawer-input"
                  type="text"
                  value={options.exportTitle}
                  onChange={e => set({ exportTitle: e.target.value })}
                  placeholder="WhatsApp Chat Export"
                />
              </div>

              <div className="cp-drawer-divider" />

              {/* Colors — full palette */}
              <SwatchRow label="Eigene Nachrichten" value={options.exporteurColor}
                swatches={SWATCHES_EXPORTEUR} onChange={c => set({ exporteurColor: c })} />
              <SwatchRow label="Andere Nachrichten" value={options.otherColor}
                swatches={SWATCHES_OTHER} onChange={c => set({ otherColor: c })} />
              <SwatchRow label="Hintergrund" value={options.backgroundColor}
                swatches={SWATCHES_BG} onChange={c => set({ backgroundColor: c })} />

              <div className="cp-drawer-divider" />

              {/* Toggles */}
              {([
                ['Systemnachrichten', 'showSystemMessages'],
                ['Zeitstempel', 'showTimestamps'],
                ['Sendernamen', 'showSenderNames'],
              ] as const).map(([label, key]) => (
                <label key={key} className="cp-toggle-row">
                  <span>{label}</span>
                  <button type="button"
                    className={`cp-toggle${options[key] ? ' on' : ''}`}
                    onClick={() => set({ [key]: !options[key] })}>
                    <span className="cp-toggle-thumb" />
                  </button>
                </label>
              ))}

              <div className="cp-drawer-divider" />

              {/* Bubble style */}
              <div className="cp-option-row">
                <span>Bubble-Form</span>
                <div className="cap-seg">
                  {(['rounded', 'square'] as const).map(s => (
                    <button key={s} type="button"
                      className={options.bubbleStyle === s ? 'active' : ''}
                      onClick={() => set({ bubbleStyle: s })}>
                      {s === 'rounded' ? '⬭ Rund' : '▭ Eckig'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="cp-drawer-divider" />

              <button type="button" className="cap-desktop-link" onClick={() => { setSettingsOpen(false); onDesktopHandoff() }}>
                🖥 Auf Desktop öffnen (Code)
              </button>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Vaul Purchase Sheet */}
      <Drawer.Root open={purchaseSheetOpen} onOpenChange={setPurchaseSheetOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="cp-drawer-overlay" />
          <Drawer.Content className="cp-drawer">
            <div className="cp-drawer-handle" />
            <Drawer.Title className="cp-drawer-title">PDF erstellen</Drawer.Title>
            <div className="cp-drawer-body">
              <button
                type="button"
                className="cp-purchase-option"
                onClick={() => { setPurchaseSheetOpen(false); onPurchase('premium_pdf_export') }}
              >
                <span className="cp-purchase-icon">📄</span>
                <div className="cp-purchase-info">
                  <span className="cp-purchase-name">PDF Export</span>
                  <span className="cp-purchase-desc">Alle Nachrichten als PDF, mit Medien-Thumbnails</span>
                </div>
                <span className="cp-purchase-price">
                  {billingProducts.find(p => p.id === 'premium_pdf_export')?.price ?? '€4,99'}
                </span>
              </button>
              {PREMIUM_PLUS_ENABLED && (
              <div className="cp-purchase-option cp-purchase-option--featured">
                <button
                  type="button"
                  className="cp-purchase-option-inner"
                  onClick={() => { setPurchaseSheetOpen(false); onPurchase('premium_plus_media') }}
                >
                  <span className="cp-purchase-icon">📦</span>
                  <div className="cp-purchase-info">
                    <span className="cp-purchase-name">PDF + Medien <span className="cp-purchase-badge">Empfohlen</span></span>
                    <span className="cp-purchase-desc">Bilder &amp; Videos als klickbare Links im PDF</span>
                    <span className="cp-purchase-bullets">1 Jahr online gehostet · jederzeit abrufbar</span>
                  </div>
                  <span className="cp-purchase-price">
                    {billingProducts.find(p => p.id === 'premium_plus_media')?.price ?? '€5,99'}
                  </span>
                </button>
                <button
                  type="button"
                  className="cp-purchase-details-toggle"
                  onClick={() => setPlusDetailsOpen(v => !v)}
                >
                  {plusDetailsOpen ? '▲ Weniger' : '▼ Details zum Medien-Hosting'}
                </button>
                {plusDetailsOpen && (
                  <div className="cf-cloud-box cf-cloud-box--inline">
                    <p className="cf-cloud-row"><strong>Formate:</strong> JPG, PNG, WebP, HEIC · MP4, MOV · MP3, OGG, AAC · PDF, DOCX, XLSX</p>
                    <p className="cf-cloud-row"><strong>Limit:</strong> 500 MB pro Export</p>
                    <p className="cf-cloud-row"><strong>Laufzeit:</strong> 1 Jahr, danach automatisch gelöscht</p>
                    <button type="button" className="cf-cloud-more-link" onClick={() => { setPurchaseSheetOpen(false); setFeaturesOpen(true) }}>
                      Vollständiger Vergleich →
                    </button>
                  </div>
                )}
              </div>
              )}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      <CapacitorFeaturesDrawer open={featuresOpen} onClose={() => setFeaturesOpen(false)} />

      {mediaSheetOpen && (
        <CapacitorMediaSheet
          mediaMode={options.mediaMode}
          onSelect={mode => { set({ mediaMode: mode }); setMediaSheetOpen(false) }}
          onClose={() => setMediaSheetOpen(false)}
        />
      )}
    </>
  )
}
